import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Sprout, LayoutDashboard } from "lucide-react";
import { navLinks, brand } from "../mock";
import { useAuth } from "../context/AuthContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const renderLink = (l) => {
    const isHash = l.href.includes("#");
    if (isHash && location.pathname === "/") {
      const [, hash] = l.href.split("#");
      return (
        <a
          key={l.label}
          href={`#${hash}`}
          className="text-sm text-white/75 hover:text-teal-300 transition-colors"
        >
          {l.label}
        </a>
      );
    }
    return (
      <Link
        key={l.label}
        to={l.href}
        className="text-sm text-white/75 hover:text-teal-300 transition-colors"
      >
        {l.label}
      </Link>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border] duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-md border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-teal-500 text-black group-hover:bg-teal-400 transition-colors">
            <Sprout className="w-5 h-5" strokeWidth={2.25} />
          </span>
          <span className="text-white font-semibold tracking-tight text-lg">
            {brand.name.split(" ")[0]}
            <span className="text-teal-400"> {brand.name.split(" ")[1]}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map(renderLink)}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {!loading && user ? (
            <>
              <span className="text-sm text-white/55 hidden xl:inline">
                Hi, {user.name.split(" ")[0]}
              </span>
              {user.church_id ? (
                <Link to="/dashboard">
                  <Button
                    variant="outline"
                    className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-10 px-4"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/signup">
                  <Button className="bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-md h-10 px-5">
                    Plant your Hub
                  </Button>
                </Link>
              )}
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-sm text-white/55 hover:text-white transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-white/75 hover:text-white transition-colors"
              >
                Sign in
              </Link>
              <Link to="/signup">
                <Button className="bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-md h-10 px-5">
                  Plant your Hub
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-white/10 px-6 py-6 space-y-4">
          {navLinks.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              onClick={() => setOpen(false)}
              className="block text-white/80 hover:text-teal-300"
            >
              {l.label}
            </Link>
          ))}
          {!loading && user ? (
            <>
              {user.church_id && (
                <Link to="/dashboard" onClick={() => setOpen(false)}>
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/5"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
              )}
              <button
                onClick={() => {
                  logout();
                  setOpen(false);
                  navigate("/");
                }}
                className="block w-full text-left text-white/70 hover:text-teal-300 text-sm py-1"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="block text-white/80 hover:text-teal-300"
              >
                Sign in
              </Link>
              <Link to="/signup" onClick={() => setOpen(false)}>
                <Button className="w-full bg-teal-500 hover:bg-teal-400 text-black font-semibold">
                  Plant your Hub
                </Button>
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
